import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { passwordSchema } from '@/api/auth/auth.schema';
import { Field } from '@/components/ui/admin/field';
import {
  InlineNote,
  PasswordField,
  PasswordStrength,
  ProfileHero,
  ReadonlyField,
  SaveBar,
  SectionCard,
} from '@/components/ui/admin/profile';
import { Content } from '@/components/ui/content';
import { Icon } from '@/components/ui/icon';
import { ContentLoader } from '@/components/ui/loaders';
import { userRoleOptions } from '@/db/schema/users';
import { useUploadFile } from '@/hooks/use-upload';
import { meta } from '@/lib/meta';
import {
  useChangePassword,
  useCurrentUser,
  useIsAuthenticated,
  useUpdateProfile,
} from '@/services/auth';

const profileFormSchema = z
  .object({
    name: z.string('Campo obrigatório!').min(1, 'Campo obrigatório!'),
    image: z.string().optional(),
    currentPassword: z.string().optional(),
    newPassword: z.string().optional(),
    confirmPassword: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.currentPassword && !data.newPassword && !data.confirmPassword) return;

    if (!data.currentPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['currentPassword'],
        message: 'Campo obrigatório!',
      });
    }

    const passwordCheck = passwordSchema.safeParse(data.newPassword);
    if (!passwordCheck.success) {
      ctx.addIssue({
        code: 'custom',
        path: ['newPassword'],
        message: passwordCheck.error.issues[0]?.message ?? 'Senha inválida!',
      });
    }

    if (data.newPassword !== data.confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: 'As senhas não conferem!',
      });
    }
  });

type ProfileFormValues = z.infer<typeof profileFormSchema>;

const monthYear = (date: Date) => {
  const month = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${date.getFullYear()}`;
};

const lastAccess = (date: Date) => {
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const isToday = date.toDateString() === new Date().toDateString();
  return isToday ? `Hoje, ${time}` : `${date.toLocaleDateString('pt-BR')}, ${time}`;
};

const daysAgo = (date: Date) => {
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days <= 0) return 'Hoje';
  return days === 1 ? 'Há 1 dia' : `Há ${days} dias`;
};

export const Route = createFileRoute('/_app/profile')({
  head: () => meta({ label: 'Meu Perfil' }),
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = useIsAuthenticated();
  const { data: current } = useCurrentUser();
  const { mutate: updateProfile, isPending: isSavingProfile } = useUpdateProfile();
  const { mutate: changePassword, isPending: isSavingPassword } = useChangePassword();
  const { mutate: upload, isPending: isUploading } = useUploadFile();

  const [justSaved, setJustSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    shouldUseNativeValidation: false,
    defaultValues: {
      name: user?.name ?? '',
      image: user?.image ?? undefined,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const isSubmitting = isSavingProfile || isSavingPassword;
  const { isDirty, errors } = form.formState;
  const hasErrors = Object.keys(errors).length > 0;

  const status = hasErrors ? 'error' : isDirty ? 'dirty' : justSaved ? 'saved' : 'clean';

  const [image, newPassword = '', confirmPassword = ''] = form.watch([
    'image',
    'newPassword',
    'confirmPassword',
  ]);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    upload(file, {
      onSuccess: (uploaded) =>
        form.setValue('image', uploaded.path, { shouldDirty: true }),
    });
  };

  const handleSubmit = (data: ProfileFormValues) => {
    const markSaved = () => {
      form.reset({
        name: data.name,
        image: data.image,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setJustSaved(true);
    };

    updateProfile(
      { name: data.name, image: data.image },
      {
        onSuccess: () => {
          if (!data.newPassword || !data.currentPassword) return markSaved();

          changePassword(
            { currentPassword: data.currentPassword, newPassword: data.newPassword },
            { onSuccess: markSaved },
          );
        },
      },
    );
  };

  const roleLabel =
    userRoleOptions.find((option) => option.value === user?.role)?.label ?? user?.role;
  const sessionStart = current?.session?.createdAt;

  return (
    <Content
      breadcrumbs={{
        title: 'Meu Perfil',
        items: [{ label: 'Meu Perfil', active: true }],
      }}
    >
      <ContentLoader show={isSubmitting || isUploading} />

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleAvatarChange}
        accept="image/*"
        className="hidden"
      />

      <form
        noValidate
        id="profile-form"
        onSubmit={form.handleSubmit(handleSubmit)}
        onChange={() => setJustSaved(false)}
        className="flex flex-col gap-6 pb-24"
      >
        <ProfileHero
          name={form.watch('name') || user?.name || ''}
          email={user?.email ?? ''}
          avatarUrl={image}
          role={roleLabel ?? ''}
          active={user?.isActive ?? true}
          onChangePhoto={() => fileInputRef.current?.click()}
          cameraIcon={<Icon name="camera" className="size-4" />}
          shieldIcon={<Icon name="shield" className="size-3" />}
          mailIcon={<Icon name="mail" className="size-4" />}
          meta={[
            {
              label: 'Membro desde',
              value: user?.createdAt ? monthYear(new Date(user.createdAt)) : '—',
            },
            {
              label: 'Último acesso',
              value: sessionStart ? lastAccess(new Date(sessionStart)) : '—',
            },
            {
              label: 'Perfil alterado',
              value: user?.updatedAt ? daysAgo(new Date(user.updatedAt)) : '—',
              tone: 'sand',
            },
          ]}
        />

        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] items-start gap-6">
          <SectionCard
            index="01"
            title="Dados pessoais"
            description="Como você aparece para os demais usuários do comando."
          >
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Field
                    {...field}
                    label="Nome"
                    required
                    autoComplete="name"
                    placeholder="Seu nome completo"
                    invalid={fieldState.invalid}
                  />
                  {fieldState.error && (
                    <span className="px-1 text-xs text-danger-soft">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />
            <ReadonlyField label="E-mail" value={user?.email ?? ''} />
            <InlineNote icon={<Icon name="info" className="size-4" />}>
              Para alterar o e-mail de acesso, solicite a outro administrador em{' '}
              <Link to="/users" className="font-semibold text-olive-ink hover:underline">
                Usuários
              </Link>
              .
            </InlineNote>
          </SectionCard>

          <SectionCard
            index="02"
            title="Segurança"
            description="Deixe em branco para manter a senha atual."
          >
            <PasswordController
              form={form}
              name="currentPassword"
              label="Senha atual"
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-4">
              <PasswordController
                form={form}
                name="newPassword"
                label="Nova senha"
                placeholder="Mín. 8 caracteres"
              />
              <PasswordController
                form={form}
                name="confirmPassword"
                label="Confirmar nova senha"
                placeholder="Repita a nova senha"
              />
            </div>
            <PasswordStrength
              password={newPassword}
              confirm={confirmPassword}
              checkIcon={<Icon name="check" className="size-3" />}
            />
          </SectionCard>
        </div>
      </form>

      <SaveBar
        status={status}
        saving={isSubmitting}
        onDiscard={() => {
          form.reset();
          setJustSaved(false);
        }}
        onSave={() => form.handleSubmit(handleSubmit)()}
        saveIcon={<Icon name="save" className="size-4" />}
      />
    </Content>
  );
}

type PasswordControllerProps = {
  form: ReturnType<typeof useForm<ProfileFormValues>>;
  name: 'currentPassword' | 'newPassword' | 'confirmPassword';
  label: string;
  placeholder?: string;
  autoComplete?: string;
};

function PasswordController({
  form,
  name,
  label,
  placeholder,
  autoComplete = 'new-password',
}: PasswordControllerProps) {
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field, fieldState }) => (
        <div className="flex flex-col gap-1.5">
          <PasswordField
            {...field}
            label={label}
            placeholder={placeholder}
            autoComplete={autoComplete}
            invalid={fieldState.invalid}
          />
          {fieldState.error && (
            <span className="px-1 text-xs text-danger-soft">
              {fieldState.error.message}
            </span>
          )}
        </div>
      )}
    />
  );
}
